import type { ReportItemType } from "@/types/report"

export type ChatReportTarget = {
  itemType: ReportItemType
  itemId: string
  itemTitle: string
  reportedUserId: string
  reportedUsername?: string
  reportedAvatarUrl?: string | null
}

export type ChatReportResolveError =
  | { kind: "no_messages" }
  | { kind: "no_other_messages" }
  | { kind: "fetch_failed"; message?: string }

export function canonicalChatRoomName(roomName: string) {
  return roomName.includes(":") ? roomName.split(":").sort().join(":") : roomName
}

export async function resolveChatReportTarget(
  roomName: string,
  currentUserId: string,
  otherProfile?: { username?: string; avatar_url?: string | null },
  otherId?: string,
): Promise<ChatReportTarget | ChatReportResolveError> {
  const res = await fetch(`/api/chat/messages?roomName=${encodeURIComponent(roomName)}&limit=20`)
  if (!res.ok) {
    return { kind: "fetch_failed", message: "Failed to load messages" }
  }

  const data = await res.json()
  const messages = Array.isArray(data?.messages) ? data.messages : []
  if (messages.length === 0) {
    return { kind: "no_messages" }
  }

  let target: { sender_id?: string } | null = null
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    const msg = messages[i]
    if (msg?.sender_id && msg.sender_id !== currentUserId) {
      target = msg
      break
    }
  }

  if (!target?.sender_id) {
    return { kind: "no_other_messages" }
  }

  const isOther = otherId && target.sender_id === otherId

  return {
    itemType: "chat",
    itemId: canonicalChatRoomName(roomName),
    itemTitle: otherProfile?.username ? `Chat with @${otherProfile.username}` : "Chat",
    reportedUserId: target.sender_id,
    reportedUsername: isOther ? otherProfile?.username : undefined,
    reportedAvatarUrl: isOther ? (otherProfile?.avatar_url ?? null) : undefined,
  }
}
